import React, { useState, useEffect } from 'react';
import { 
  Box, 
  Typography, 
  Button, 
  Container, 
  Grid, 
  Card, 
  CardContent, 
  CardActions,
  IconButton,
  useTheme,
  useMediaQuery,
  Paper,
  CircularProgress,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow
} from '@mui/material';
import { 
  HeartPulse, 
  Zap, 
  ChevronRight,
  Activity,
  Search,
  X,
  Thermometer,
  Weight,
  Ruler,
  ChevronLeft
} from 'lucide-react';
import { styled } from '@mui/material/styles';
import { motion } from 'framer-motion';
// Importar el servicio de paciente
import agthealthPatientService from '../services/agthealthPatientService';
// Importar recharts para gráficos de tendencias
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from 'recharts';
import { format, parseISO } from 'date-fns';
import { es } from 'date-fns/locale';
import AGTFloatingChat from '../utils/AGTFloatingChat';
import { useTranslation } from 'react-i18next';
import Medications from '../medications/Medications';
import Supplements from '../supplements/Supplements';
import Conditions from '../conditions/Conditions';

// ===========================================
// 🎬 VARIANTES DE ANIMACIÓN TIPO macOS
// ===========================================
const containerVariants = {
  hidden: {
    opacity: 0,
    y: 20
  },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.5,
      ease: [0.25, 0.46, 0.45, 0.94],
      staggerChildren: 0.08
    }
  }
};

const itemVariants = {
  hidden: {
    opacity: 0,
    y: 15
  },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.4,
      ease: [0.25, 0.46, 0.45, 0.94]
    }
  }
};

// Constantes de color para modo wallpaper
const WALLPAPER_COLORS = {
  bg: 'rgba(128, 128, 128, 0.3)',
  bgLight: 'rgba(128, 128, 128, 0.25)',
  border: 'rgba(128, 128, 128, 0.3)',
  text: '#FFFFFF',
  textSecondary: 'rgba(255, 255, 255, 0.7)'
};

const HEALTH_GREEN = '#4CAF50';

// -------------------------------------
// Estilos de la barra de Tabs (NavigationBar)
// -------------------------------------
const StyledTabs = styled(Box)(({ theme }) => ({
  minHeight: '40px',
  height: '40px',
  background: theme.palette.actualMode === 'wallpaper'
    ? WALLPAPER_COLORS.bg
    : (theme.palette.mode === 'dark'
      ? 'rgba(40, 40, 42, 0.94)'
      : 'rgba(242, 242, 247, 1)'),
  backdropFilter: theme.palette.actualMode === 'wallpaper' ? 'blur(10px)' : 'none',
  WebkitBackdropFilter: theme.palette.actualMode === 'wallpaper' ? 'blur(10px)' : 'none',
  padding: '6px',
  borderRadius: '24px',
  position: 'relative',
  display: 'flex',
  alignItems: 'center',
  overflow: 'hidden',
}));

// -------------------------------------
// El "fondo" animado debajo del tab activo
// -------------------------------------
const TabIndicator = styled('div')(({ theme }) => ({
  position: 'absolute',
  height: '28px',
  width: '100px',
  borderRadius: '24px',
  backgroundColor: theme.palette.actualMode === 'wallpaper'
    ? WALLPAPER_COLORS.bgLight
    : (theme.palette.mode === 'dark'
      ? 'rgba(118, 118, 128, 0.24)'
      : '#FFFFFF'),
  transition: 'transform 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
  zIndex: 0,
  boxShadow: theme.palette.actualMode === 'wallpaper'
    ? 'none'
    : (theme.palette.mode === 'dark'
      ? 'none'
      : '0 2px 8px rgba(0, 0, 0, 0.04)'),
}));

// -------------------------------------
// Estilos de cada Tab individual
// -------------------------------------
const StyledTab = styled(Button, {
  shouldForwardProp: (prop) => prop !== 'active'
})(({ theme, active }) => ({
  minHeight: '28px',
  height: '28px',
  minWidth: '100px',
  padding: '0 16px',
  borderRadius: '24px',
  color: theme.palette.actualMode === 'wallpaper'
    ? (active ? WALLPAPER_COLORS.text : WALLPAPER_COLORS.textSecondary)
    : (theme.palette.mode === 'dark'
      ? 'rgba(255, 255, 255, 0.7)'
      : '#666666'),
  fontSize: '14px',
  fontWeight: 500,
  textTransform: 'none',
  zIndex: 1,
  transition: 'all 0.2s',
  backgroundColor: 'transparent',
  '&:hover': {
    backgroundColor: 'transparent',
    color: theme.palette.actualMode === 'wallpaper' ? WALLPAPER_COLORS.text : HEALTH_GREEN,
  },
  ...(active && {
    color: theme.palette.actualMode === 'wallpaper' ? WALLPAPER_COLORS.text : HEALTH_GREEN,
  }),
  '& .MuiButton-startIcon': {
    marginRight: '4px',
    marginLeft: '-4px',
    '& svg': {
      fontSize: '16px',
    }
  },
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
}));

// -------------------------------------
// Botones de flecha / más (+)
// -------------------------------------
const NavigationButton = styled('button')(({ theme }) => ({
  minHeight: '28px',
  height: '28px',
  width: '28px',
  minWidth: '28px',
  padding: '0',
  marginRight: '6px',
  marginLeft: '6px',
  border: 'none',
  borderRadius: '30px',
  backgroundColor: 'transparent',
  color: theme.palette.actualMode === 'wallpaper' ? WALLPAPER_COLORS.text : HEALTH_GREEN,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  cursor: 'pointer',
  zIndex: 1,
  transition: 'all 0.2s',
  outline: 'none',
  '&:hover': {
    backgroundColor: 'transparent',
    opacity: 0.8,
  },
  '&:active': {
    transform: 'scale(0.95)',
    backgroundColor: 'transparent',
  },
}));

// Styled components
const PageContainer = styled(Container)(({ theme }) => ({
  padding: theme.spacing(3),
  maxWidth: '1200px',
  backgroundColor: theme.palette.actualMode === 'wallpaper'
    ? 'transparent'
    : (theme.palette.mode === 'dark' ? '#000000' : '#FFFFFF'),
  minHeight: '100vh',
  [theme.breakpoints.down('sm')]: {
    padding: theme.spacing(2, 1),
  }
}));

const SectionTitle = styled(Typography)(({ theme }) => ({
  fontWeight: 700,
  marginBottom: theme.spacing(2),
  fontSize: '1.5rem',
  color: theme.palette.actualMode === 'wallpaper' ? WALLPAPER_COLORS.text : 'inherit',
  [theme.breakpoints.down('sm')]: {
    fontSize: '1.25rem',
  }
}));

const HealthCard = styled(Card)(({ theme, color = '#4CAF50' }) => ({
  borderRadius: 16,
  boxShadow: theme.palette.actualMode === 'wallpaper' ? 'none' : '0 4px 12px rgba(0,0,0,0.05)',
  transition: 'all 0.3s ease',
  overflow: 'hidden',
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  '&:hover': {
    transform: 'translateY(-4px)',
    boxShadow: theme.palette.actualMode === 'wallpaper' ? 'none' : '0 8px 24px rgba(0,0,0,0.12)',
  },
  border: theme.palette.actualMode === 'wallpaper'
    ? `1px solid ${WALLPAPER_COLORS.border}`
    : `1px solid ${theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.06)'}`,
  backgroundColor: theme.palette.actualMode === 'wallpaper'
    ? WALLPAPER_COLORS.bg
    : (theme.palette.mode === 'dark' ? '#1C1C1E' : '#FFFFFF'),
  backdropFilter: theme.palette.actualMode === 'wallpaper' ? 'blur(20px)' : 'none',
  WebkitBackdropFilter: theme.palette.actualMode === 'wallpaper' ? 'blur(20px)' : 'none',
}));

// Nuevo componente para los mini contenedores tipo app
const HealthAppContainer = styled(Box)(({ theme, color = '#4CAF50' }) => ({
  borderRadius: 12,
  padding: theme.spacing(1.5),
  backgroundColor: theme.palette.actualMode === 'wallpaper'
    ? WALLPAPER_COLORS.bgLight
    : (theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.02)'),
  border: theme.palette.actualMode === 'wallpaper'
    ? `1px solid ${WALLPAPER_COLORS.border}`
    : `1px solid ${theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.06)'}`,
  transition: 'all 0.2s ease',
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  backdropFilter: theme.palette.actualMode === 'wallpaper' ? 'blur(10px)' : 'none',
  WebkitBackdropFilter: theme.palette.actualMode === 'wallpaper' ? 'blur(10px)' : 'none',
  '&:hover': {
    transform: 'translateY(-2px)',
    boxShadow: theme.palette.actualMode === 'wallpaper' ? 'none' : '0 4px 8px rgba(0,0,0,0.08)',
    backgroundColor: theme.palette.actualMode === 'wallpaper'
      ? 'rgba(128, 128, 128, 0.35)'
      : (theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.04)'),
  },
}));

const IconContainer = styled(Box)(({ theme, color = '#4CAF50' }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  color: color,
  marginRight: theme.spacing(1.5),
}));

// Versión más pequeña del IconContainer para los mini contenedores
const AppIconContainer = styled(Box)(({ theme, color = '#4CAF50' }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  color: color,
  marginBottom: theme.spacing(1),
}));

const HealthMetric = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  padding: theme.spacing(1.5),
  borderRadius: 12,
  backgroundColor: theme.palette.actualMode === 'wallpaper'
    ? WALLPAPER_COLORS.bgLight
    : (theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.02)'),
  marginBottom: theme.spacing(1.5),
}));

const SearchBar = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  backgroundColor: theme.palette.actualMode === 'wallpaper'
    ? WALLPAPER_COLORS.bg
    : (theme.palette.mode === 'dark'
      ? 'rgba(40, 40, 42, 0.94)'
      : 'rgba(242, 242, 247, 1)'),
  backdropFilter: theme.palette.actualMode === 'wallpaper' ? 'blur(10px)' : 'none',
  WebkitBackdropFilter: theme.palette.actualMode === 'wallpaper' ? 'blur(10px)' : 'none',
  borderRadius: 28,
  padding: '0 16px',
  height: '40px',
  marginBottom: theme.spacing(3),
  marginTop: theme.spacing(1),
  width: '100%',
  maxWidth: '300px',
  margin: '8px auto 24px',
}));

// Contenido de cada pestaña
const TabPanel = (props) => {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`health-tabpanel-${index}`}
      aria-labelledby={`health-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ pt: 3 }}>
          {children}
        </Box>
      )}
    </div>
  );
};

// Nuevo componente para el diálogo de detalles del registro médico
const MedicalRecordDetailDialog = ({ open, record, onClose }) => {
  const theme = useTheme();
  const { t } = useTranslation();
  
  if (!record) return null;

  // Mapeo de iconos para los diferentes tipos de signos vitales
  const getVitalSignIcon = (key) => {
    const iconMap = {
      heartRate: <HeartPulse size={18} />,
      systolicBP: <Activity size={18} />,
      diastolicBP: <Activity size={18} />,
      bloodGlucose: <Zap size={18} />,
      oxygenSaturation: <Zap size={18} />,
      temperature: <Thermometer size={18} />,
      weight: <Weight size={18} />,
      height: <Ruler size={18} />
    };
    return iconMap[key] || <Activity size={18} />;
  };

  // Mapeo de nombres amigables para los signos vitales
  const getVitalSignName = (key) => {
    const nameMap = {
      heartRate: t('vitalSigns.heartRate'),
      systolicBP: t('vitalSigns.systolicBP'),
      diastolicBP: t('vitalSigns.diastolicBP'),
      bloodGlucose: t('vitalSigns.bloodGlucose'),
      oxygenSaturation: t('vitalSigns.oxygenSaturation'),
      temperature: t('vitalSigns.temperature'),
      weight: t('vitalSigns.weight'),
      height: t('vitalSigns.height')
    };
    return nameMap[key] || key;
  };

  // Mapeo de unidades para los signos vitales
  const getVitalSignUnit = (key) => {
    const unitMap = {
      heartRate: t('units.bpm'),
      systolicBP: t('units.mmHg'),
      diastolicBP: t('units.mmHg'),
      bloodGlucose: t('units.mgdl'),
      oxygenSaturation: t('units.percentage'),
      temperature: t('units.celsius'),
      weight: t('units.kg'),
      height: t('units.cm')
    };
    return unitMap[key] || '';
  };

  // Función para formatear fecha en formato "Lunes 14, Mayo 2025. 09:28"
  const formatDateFull = (dateString) => {
    try {
      const date = parseISO(dateString);
      // Formato: "Lunes 14, Mayo 2025. 09:28"
      return format(date, "EEEE d, MMMM yyyy. HH:mm", { locale: es });
    } catch (error) {
      return dateString;
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
          boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
          backgroundColor: theme.palette.actualMode === 'wallpaper'
            ? WALLPAPER_COLORS.bg
            : undefined,
          backdropFilter: theme.palette.actualMode === 'wallpaper' ? 'blur(20px)' : 'none',
          WebkitBackdropFilter: theme.palette.actualMode === 'wallpaper' ? 'blur(20px)' : 'none',
          border: theme.palette.actualMode === 'wallpaper' ? `1px solid ${WALLPAPER_COLORS.border}` : undefined,
        }
      }}
    >
      <DialogTitle sx={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        borderBottom: theme.palette.actualMode === 'wallpaper'
          ? `1px solid ${WALLPAPER_COLORS.border}`
          : `1px solid ${theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.06)'}`,
        pb: 1
      }}>
        <Box>
          <Typography variant="h6" fontWeight={600}>
            {record.diagnosis || t('medicalRecords.record')}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {formatDateFull(record.date)}
          </Typography>
        </Box>
        <IconButton onClick={onClose} size="small">
          <X size={20} />
        </IconButton>
      </DialogTitle>
      <DialogContent sx={{ py: 3 }}>
        {/* Sección de diagnóstico y tratamiento */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="subtitle1" fontWeight={600} gutterBottom>
            {t('medicalRecordDetail.diagnosis')}
          </Typography>
          <Typography variant="body1" paragraph>
            {record.diagnosis || t('medicalRecordDetail.noDiagnosis')}
          </Typography>

          {record.treatment && (
            <>
              <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                {t('medicalRecordDetail.treatmentPlan')}
              </Typography>
              <Typography variant="body1" paragraph>
                {record.treatment}
              </Typography>
            </>
          )}
        </Box>

        {/* Tabla completa de signos vitales */}
        <Typography variant="subtitle1" fontWeight={600} gutterBottom>
          {t('medicalRecordDetail.vitalSigns')}
        </Typography>
        
        {record.vitalSigns && Object.keys(record.vitalSigns).length > 0 ? (
          <TableContainer component={Paper} sx={{ borderRadius: 2, mb: 3 }}>
            <Table sx={{ minWidth: 650 }} aria-label="vital signs table">
              <TableHead>
                <TableRow sx={{
                  backgroundColor: theme.palette.actualMode === 'wallpaper'
                    ? WALLPAPER_COLORS.bgLight
                    : (theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.02)')
                }}>
                  <TableCell>{t('medicalRecordDetail.parameter')}</TableCell>
                  <TableCell align="right">{t('medicalRecordDetail.value')}</TableCell>
                  <TableCell align="right">{t('medicalRecordDetail.referenceRange')}</TableCell>
                  <TableCell align="right">{t('medicalRecordDetail.status')}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {Object.entries(record.vitalSigns).map(([key, value]) => {
                  // Solo mostrar si el valor existe
                  if (value === undefined || value === null) return null;
                  
                  // Determinar el estado (normal, alto, bajo)
                  let status = t('status.normal');
                  let statusColor = theme.palette.actualMode === 'wallpaper' ? WALLPAPER_COLORS.text : '#4CAF50'; // blanco en wallpaper, verde en otros modos

                  // Rangos de referencia simplificados (ajustar según necesidades reales)
                  const referenceRanges = {
                    heartRate: '60-100',
                    systolicBP: '90-120',
                    diastolicBP: '60-80',
                    bloodGlucose: '70-100',
                    oxygenSaturation: '95-100',
                    temperature: '36.1-37.2',
                    // Los demás dependen de la persona
                  };

                  // Lógica simplificada para determinar estado
                  if (key === 'heartRate') {
                    if (value < 60) {
                      status = t('status.low');
                      statusColor = theme.palette.actualMode === 'wallpaper' ? WALLPAPER_COLORS.text : '#FF9800';
                    } else if (value > 100) {
                      status = t('status.high');
                      statusColor = theme.palette.actualMode === 'wallpaper' ? WALLPAPER_COLORS.text : '#F44336';
                    }
                  } else if (key === 'systolicBP') {
                    if (value < 90) {
                      status = t('status.low');
                      statusColor = theme.palette.actualMode === 'wallpaper' ? WALLPAPER_COLORS.text : '#FF9800';
                    } else if (value > 120) {
                      status = t('status.high');
                      statusColor = theme.palette.actualMode === 'wallpaper' ? WALLPAPER_COLORS.text : '#F44336';
                    }
                  }
                  // Continuar con más lógica para otros signos vitales
                  
                  return (
                    <TableRow key={key}>
                      <TableCell component="th" scope="row">
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          <IconContainer color={statusColor} sx={{ mr: 1 }}>
                            {getVitalSignIcon(key)}
                          </IconContainer>
                          {getVitalSignName(key)}
                        </Box>
                      </TableCell>
                      <TableCell align="right">
                        {value} {getVitalSignUnit(key)}
                      </TableCell>
                      <TableCell align="right">
                        {referenceRanges[key] || t('dates.na')} {getVitalSignUnit(key)}
                      </TableCell>
                      <TableCell align="right">
                        <Chip 
                          label={status} 
                          size="small" 
                          sx={{ 
                            bgcolor: `${statusColor}20`, 
                            color: statusColor,
                            fontWeight: 500
                          }} 
                        />
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        ) : (
          <Typography variant="body2" color="text.secondary">
            {t('medicalRecordDetail.noVitalSigns')}
          </Typography>
        )}
        
        {/* Notas adicionales */}
        {record.notes && (
          <>
            <Typography variant="subtitle1" fontWeight={600} gutterBottom>
              {t('medicalRecordDetail.additionalNotes')}
            </Typography>
            <Typography variant="body1">
              {record.notes}
            </Typography>
          </>
        )}
      </DialogContent>
      <DialogActions sx={{ p: 2 }}>
        <Button 
          onClick={onClose} 
          variant="outlined" 
          sx={{ borderRadius: 28, textTransform: 'none' }}
        >
          {t('medicalRecordDetail.close')}
        </Button>
        <Button
          variant="contained"
          sx={{
            borderRadius: 28,
            textTransform: 'none',
            bgcolor: theme.palette.actualMode === 'wallpaper' ? WALLPAPER_COLORS.bg : '#4CAF50',
            color: theme.palette.actualMode === 'wallpaper' ? WALLPAPER_COLORS.text : undefined,
            backdropFilter: theme.palette.actualMode === 'wallpaper' ? 'blur(10px)' : 'none',
            WebkitBackdropFilter: theme.palette.actualMode === 'wallpaper' ? 'blur(10px)' : 'none',
            border: theme.palette.actualMode === 'wallpaper' ? `1px solid ${WALLPAPER_COLORS.border}` : 'none',
            '&:hover': {
              bgcolor: theme.palette.actualMode === 'wallpaper' ? WALLPAPER_COLORS.bgLight : '#43A047'
            }
          }}
        >
          {t('medicalRecordDetail.download')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// Componente de NavigationBar estilo Apple para las pestañas
const HealthNavigationBar = ({ activeTab, onChangeTab }) => {
  const { t } = useTranslation();
 
  // Items de navegación - Sin iconos
  const navItems = [
    { id: 0, label: t('tabs.summary') },
    { id: 1, label: t('tabs.health') },
    { id: 2, label: t('tabs.trends') },
    { id: 3, label: 'Medicamentos' },
    { id: 4, label: 'Suplementos' }
  ];
 
  // Calcular la posición del indicador con un ancho fijo
  const getIndicatorStyle = () => {
    // Ancho fijo para cada tab
    const baseWidth = 120;
    // Calcular el desplazamiento basado en el índice activo
    const offset = activeTab * baseWidth;
    return { 
      transform: `translateX(${offset}px)`, 
      width: `${baseWidth}px` 
    };
  };
 
  return (
    <Box sx={{ display: 'flex', justifyContent: 'center', width: '100%', my: 2 }}>
      <StyledTabs>
        {/* Indicador animado */}
        <TabIndicator style={getIndicatorStyle()} />
 
        {/* Tabs */}
        {navItems.map((item) => (
          <StyledTab
            key={item.id}
            active={activeTab === item.id}
            onClick={() => onChangeTab(item.id)}
            disableRipple
            sx={{ minWidth: '120px', width: '120px' }} // Ancho fijo
          >
            {item.label}
          </StyledTab>
        ))}
      </StyledTabs>
    </Box>
  );
};

// Componente de NavigationBar para las tendencias de salud
const TrendNavigationBar = ({ activeMetric, onChangeMetric }) => {
  const theme = useTheme();
  const { t } = useTranslation();
  
  // Métricas disponibles para tendencias (modificado según los requisitos)
  const metrics = [
    { id: 'heartRate', label: t('trends.heart') },
    { id: 'bloodGlucose', label: t('trends.glucose') },
    { id: 'systolicBP', label: t('trends.pressure') },
    { id: 'oxygenSaturation', label: t('trends.oxygen') },
    { id: 'temperature', label: t('trends.temperature') },
    { id: 'weight', label: t('trends.weight') },
    { id: 'height', label: t('trends.height') }
  ];

  // Estados para control de navegación en móvil
  const [visibleRange, setVisibleRange] = useState({ start: 0, end: 3 });
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  // Mostrar solo un subconjunto de métricas en móvil
  const visibleMetrics = isMobile 
    ? metrics.slice(visibleRange.start, visibleRange.end)
    : metrics;

  // Mostrar flechas de navegación si hay más métricas
  const showLeftArrow = isMobile && visibleRange.start > 0;
  const showRightArrow = isMobile && visibleRange.end < metrics.length;

  // Navegar a la izquierda
  const handleScrollLeft = () => {
    if (visibleRange.start > 0) {
      setVisibleRange({
        start: visibleRange.start - 1,
        end: visibleRange.end - 1
      });
    }
  };

  // Navegar a la derecha
  const handleScrollRight = () => {
    if (visibleRange.end < metrics.length) {
      setVisibleRange({
        start: visibleRange.start + 1,
        end: visibleRange.end + 1
      });
    }
  };

  // Calcular la posición del indicador
  const getIndicatorStyle = () => {
    // Encontrar la posición relativa del métrico activo
    const relativeIndex = visibleMetrics.findIndex(m => m.id === activeMetric);
    if (relativeIndex === -1) return {}; // No visible
    
    const baseWidth = 120; // ancho base de cada tab
    const offset = relativeIndex * baseWidth;
    return { transform: `translateX(${offset}px)`, width: `${baseWidth}px` };
  };

  return (
    <Box sx={{ display: 'flex', justifyContent: 'center', width: '100%', mb: 3 }}>
      <StyledTabs sx={{ width: isMobile ? 360 : 'auto' }}>
        {/* Flecha izquierda */}
        {showLeftArrow && (
          <NavigationButton onClick={handleScrollLeft}>
            <ChevronLeft size={16} strokeWidth={2.5} />
          </NavigationButton>
        )}

        {/* Indicador animado */}
        <TabIndicator style={getIndicatorStyle()} />

        {/* Tabs de métricas - Sin iconos */}
        {visibleMetrics.map((metric) => (
          <StyledTab
            key={metric.id}
            active={activeMetric === metric.id}
            onClick={() => onChangeMetric(metric.id)}
            disableRipple
            sx={{ minWidth: '120px' }}
          >
            {metric.label}
          </StyledTab>
        ))}

        {/* Flecha derecha */}
        {showRightArrow && (
          <NavigationButton onClick={handleScrollRight}>
            <ChevronRight size={16} strokeWidth={2.5} />
          </NavigationButton>
        )}
      </StyledTabs>
    </Box>
  );
};

const PatientView = ({ user }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [tabValue, setTabValue] = useState(0);
  const [loading, setLoading] = useState(false);
  const [records, setRecords] = useState([]);
  const [latestRecord, setLatestRecord] = useState(null);
  const [trends, setTrends] = useState({
    heartRate: [],
    bloodGlucose: [],
    systolicBP: [],
    diastolicBP: [],
    oxygenSaturation: [],
    temperature: [],
    weight: [],
    height: []
  });
  const [activeMetric, setActiveMetric] = useState('heartRate');
  const [trendPeriod, setTrendPeriod] = useState('month');
  const { t } = useTranslation();
 
  // Estado para citas médicas
  const [appointments, setAppointments] = useState({
    upcoming: [],
    past: []
  });
 
  // Estado para el diálogo de detalles del registro médico
  const [detailDialogOpen, setDetailDialogOpen] = useState(false);
  const [selectedRecord, setSelectedRecord] = useState(null);
 
  // Cargar registros médicos y citas cuando se monte el componente
  useEffect(() => {
    loadMedicalRecords();
    loadAppointments();
  }, []);
 
  // Cargar datos de tendencias cuando cambie la pestaña o la métrica
  useEffect(() => {
    if (tabValue === 2) {
      loadTrendData(activeMetric, trendPeriod);
    }
  }, [tabValue, activeMetric, trendPeriod]);

  // Función para cargar registros médicos
  const loadMedicalRecords = async () => {
    setLoading(true);
    try {
      const response = await agthealthPatientService.getPatientRecords();
  
      if (response.success && Array.isArray(response.data)) {
        setRecords(response.data);
        // Establecer el registro más reciente
        if (response.data.length > 0) {
          // Ordenar por fecha y tomar el más reciente
          const sortedRecords = [...response.data].sort((a, b) => 
            new Date(b.date) - new Date(a.date)
          );
          setLatestRecord(sortedRecords[0]);
        }
      } else if (response.success && response.data) {
        // Si data no es un array pero existe
        const recordsArray = Array.isArray(response.data) ? response.data : [response.data];
        setRecords(recordsArray);
        setLatestRecord(recordsArray[0]);
      } else {
        // Solo loguear si no es el mensaje de "no hay registros disponibles"
        if (response.error && !response.error.includes('No hay registros')) {
          console.error("Error al cargar registros médicos:", response.error);
        }
        setRecords([]);
      }
    } catch (error) {
      // Solo loguear errores que no sean por falta de datos
      if (error.message && !error.message.includes('No hay registros')) {
        console.error("Error al cargar registros médicos:", error);
      }
      setRecords([]);
    } finally {
      setLoading(false);
    }
  };

  const loadAppointments = async () => {
    setLoading(true);
    try {
      const response = await agthealthPatientService.getPatientAppointments();
 
      if (response.success && response.data) {
        setAppointments(response.data);
      } else {
        // Solo loguear si no es el mensaje de "no hay citas disponibles"
        if (response.error && !response.error.includes('No hay citas')) {
          console.error("Error al cargar citas médicas:", response.error);
        }
        setAppointments({ upcoming: [], past: [] });
      }
    } catch (error) {
      // Solo loguear errores que no sean por falta de datos
      if (error.message && !error.message.includes('No hay citas')) {
        console.error("Error al cargar citas médicas:", error);
      }
      setAppointments({ upcoming: [], past: [] });
    } finally {
      setLoading(false);
    }
  };
 
  // Función para formatear fecha de cita
  const formatAppointmentDate = (dateString) => {
    try {
      const date = parseISO(dateString);
      const today = new Date();
      const tomorrow = new Date();
      tomorrow.setDate(today.getDate() + 1);
 
      // Si es hoy
      if (format(date, 'yyyy-MM-dd') === format(today, 'yyyy-MM-dd')) {
        return `${t('dates.today')} ${format(date, 'HH:mm')}`;
      }
      // Si es mañana
      else if (format(date, 'yyyy-MM-dd') === format(tomorrow, 'yyyy-MM-dd')) {
        return `${t('dates.tomorrow')} ${format(date, 'HH:mm')}`;
      }
      // Si es dentro de esta semana
      else if (date < new Date(today.getTime() + 7 * 24 * 60 * 60 * 1000)) {
        return format(date, "EEEE', 'HH:mm", { locale: es });
      }
      // Para otras fechas
      else {
        return format(date, "d 'de' MMMM', 'HH:mm", { locale: es });
      }
    } catch (error) {
      return dateString;
    }
  };

  // Función para cargar datos de tendencias
  const loadTrendData = async (metric, period) => {
    setLoading(true);
    try {
      const response = await agthealthPatientService.getPatientRecordTrends(metric, period);
      
      if (response.success && response.data) {
        // Asegurar que los datos sean un array
        const trendData = Array.isArray(response.data) ? response.data : [];
        
        setTrends(prevTrends => ({
          ...prevTrends,
          [metric]: trendData
        }));
      } else {
        console.error(`Error al cargar tendencias para ${metric}:`, response.error || 'No data');
        setTrends(prevTrends => ({
          ...prevTrends,
          [metric]: []
        }));
      }
    } catch (error) {
      console.error(`Error al cargar tendencias para ${metric}:`, error);
      setTrends(prevTrends => ({
        ...prevTrends,
        [metric]: []
      }));
    } finally {
      setLoading(false);
    }
  };

  // Función para manejar el cambio de pestañas
  const handleTabChange = (newValue) => {
    setTabValue(newValue);
  };

  // Función para formatear fecha en formato "Lunes 14, Mayo 2025. 09:28"
  const formatDateFull = (dateString) => {
    try {
      const date = parseISO(dateString);
      // Primera letra en mayúscula
      const formattedDate = format(date, "EEEE d, MMMM yyyy. HH:mm", { locale: es });
      return formattedDate.charAt(0).toUpperCase() + formattedDate.slice(1);
    } catch (error) {
      return dateString;
    }
  };

  // Función para abrir el diálogo de detalles
  const handleOpenDetailDialog = (record) => {
    setSelectedRecord(record);
    setDetailDialogOpen(true);
  };

  // Función para cerrar el diálogo de detalles
  const handleCloseDetailDialog = () => {
    setDetailDialogOpen(false);
  };

  // Función para obtener los valores vitales del registro más reciente
  const getLatestVitalSigns = () => {
    if (!latestRecord || !latestRecord.vitalSigns) return [];
    
    // Definimos el orden y los iconos/colores para cada tipo de signo vital
    const vitalSignsConfig = [
      { key: "heartRate", title: t('vitalSigns.heartRate'), unit: t('units.bpm'), icon: <HeartPulse size={18} />, color: theme.palette.actualMode === 'wallpaper' ? WALLPAPER_COLORS.text : "#4CAF50" },
      { key: "systolicBP", title: t('vitalSigns.systolicBP'), unit: t('units.mmHg'), icon: <Activity size={18} />, color: theme.palette.actualMode === 'wallpaper' ? WALLPAPER_COLORS.text : "#FF5722" },
      { key: "diastolicBP", title: t('vitalSigns.diastolicBP'), unit: t('units.mmHg'), icon: <Activity size={18} />, color: theme.palette.actualMode === 'wallpaper' ? WALLPAPER_COLORS.text : "#FF9800" },
      { key: "bloodGlucose", title: t('vitalSigns.bloodGlucose'), unit: t('units.mgdl'), icon: <Zap size={18} />, color: theme.palette.actualMode === 'wallpaper' ? WALLPAPER_COLORS.text : "#2196F3" },
      { key: "oxygenSaturation", title: t('vitalSigns.oxygenSaturation'), unit: t('units.percentage'), icon: <Zap size={18} />, color: theme.palette.actualMode === 'wallpaper' ? WALLPAPER_COLORS.text : "#9C27B0" },
      { key: "temperature", title: t('vitalSigns.temperature'), unit: t('units.celsius'), icon: <Thermometer size={18} />, color: theme.palette.actualMode === 'wallpaper' ? WALLPAPER_COLORS.text : "#E91E63" },
      { key: "weight", title: t('vitalSigns.weight'), unit: t('units.kg'), icon: <Weight size={18} />, color: theme.palette.actualMode === 'wallpaper' ? WALLPAPER_COLORS.text : "#795548" },
      { key: "height", title: t('vitalSigns.height'), unit: t('units.cm'), icon: <Ruler size={18} />, color: theme.palette.actualMode === 'wallpaper' ? WALLPAPER_COLORS.text : "#607D8B" }
    ];

    // Filtramos solo los signos vitales que existen en el registro
    return vitalSignsConfig
      .filter(config => latestRecord.vitalSigns[config.key] !== undefined && latestRecord.vitalSigns[config.key] !== null)
      .map(config => ({
        title: config.title,
        value: `${latestRecord.vitalSigns[config.key]} ${config.unit}`,
        icon: config.icon,
        color: config.color
      }));
  };

  // Nombres descriptivos para las métricas
  const getMetricTitle = (metric) => {
    const titles = {
      heartRate: t('trends.heartRateTitle'),
      bloodGlucose: t('trends.bloodGlucoseTitle'),
      systolicBP: t('trends.bloodPressureTitle'),
      oxygenSaturation: t('trends.oxygenSaturationTitle'),
      temperature: t('trends.temperatureTitle'),
      weight: t('trends.weightTitle'),
      height: t('trends.heightTitle')
    };
    return titles[metric] || metric;
  };

  return (
    <PageContainer
      component={motion.div}
      variants={containerVariants}
      initial="hidden"
      animate="visible"
    >
      {/* Header - Centrado */}
      <Box
        component={motion.div}
        variants={itemVariants}
        sx={{ mb: 4, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}
      >
        <Box sx={{ textAlign: 'center' }}>
          <Typography
            variant="h4"
            sx={{
              fontWeight: 700,
              fontSize: isMobile ? '1.5rem' : '2rem',
              textAlign: 'center',
              color: theme.palette.actualMode === 'wallpaper' ? WALLPAPER_COLORS.text : 'inherit'
            }}
          >
            {t('appName')}
            <motion.span
              initial={{ opacity: 0, scale: 0 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.5, type: "spring" }}
              style={{
                color: HEALTH_GREEN,
                fontSize: isMobile ? '1.25rem' : '1.75rem',
                fontWeight: 700,
                marginLeft: '2px',
                position: 'relative',
                top: '-6px'
              }}
            >
              +
            </motion.span>
          </Typography>
          <Typography
            variant="body2"
            sx={{
              textAlign: 'center',
              color: theme.palette.actualMode === 'wallpaper'
                ? WALLPAPER_COLORS.textSecondary
                : 'text.secondary'
            }}
          >
            {t('tagline')}
          </Typography>
        </Box>
      </Box>

      {/* Search bar */}
      <Box
        component={motion.div}
        variants={itemVariants}
        sx={{ display: 'flex', justifyContent: 'center', width: '100%' }}
      >
        <SearchBar>
          <Search
            size={20}
            color={theme.palette.actualMode === 'wallpaper' ? WALLPAPER_COLORS.text : HEALTH_GREEN}
            style={{ marginRight: 8 }}
          />
          <Typography
            variant="body2"
            sx={{
              color: theme.palette.actualMode === 'wallpaper'
                ? WALLPAPER_COLORS.textSecondary
                : 'text.secondary'
            }}
          >
            {t('search')}
          </Typography>
        </SearchBar>
      </Box>

      {/* Navegación mejorada estilo Apple */}
      <Box component={motion.div} variants={itemVariants}>
        <HealthNavigationBar activeTab={tabValue} onChangeTab={handleTabChange} />
      </Box>

      {/* Contenido de la pestaña de Resumen */}
      <TabPanel value={tabValue} index={0}>
        <SectionTitle variant="h5">
          {t('healthSummary.title')}
        </SectionTitle>
        <Grid container spacing={2} sx={{ mb: 4 }}>
          <Grid item xs={12}>
            <Card sx={{
              borderRadius: 3,
              boxShadow: theme.palette.actualMode === 'wallpaper' ? 'none' : '0 4px 12px rgba(0,0,0,0.05)',
              backgroundColor: theme.palette.actualMode === 'wallpaper'
                ? WALLPAPER_COLORS.bg
                : (theme.palette.mode === 'dark' ? '#1C1C1E' : '#FFFFFF'),
              backdropFilter: theme.palette.actualMode === 'wallpaper' ? 'blur(20px)' : 'none',
              WebkitBackdropFilter: theme.palette.actualMode === 'wallpaper' ? 'blur(20px)' : 'none',
              border: theme.palette.actualMode === 'wallpaper' ? `1px solid ${WALLPAPER_COLORS.border}` : 'none',
              overflow: 'visible'
            }}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <IconContainer color={theme.palette.actualMode === 'wallpaper' ? WALLPAPER_COLORS.text : HEALTH_GREEN}>
                    <Activity size={24} />
                  </IconContainer>
                  <Box sx={{ flex: 1 }}>
                    <Typography
                      variant="h6"
                      fontWeight={600}
                      sx={{
                        color: theme.palette.actualMode === 'wallpaper' ? WALLPAPER_COLORS.text : 'inherit'
                      }}
                    >
                      {t('healthSummary.todayStatus')}
                    </Typography>
                    {latestRecord && (
                      <Typography
                        variant="caption"
                        sx={{
                          color: theme.palette.actualMode === 'wallpaper'
                            ? WALLPAPER_COLORS.textSecondary
                            : 'text.secondary'
                        }}
                      >
                        {t('healthSummary.lastUpdated')} {formatDateFull(latestRecord.date)}
                      </Typography>
                    )}
                  </Box>
                </Box>
                
                {/* Contenedores tipo app para cada métrica de salud del último registro */}
                <Grid container spacing={2}>
                  {loading ? (
                    <Grid item xs={12} sx={{ textAlign: 'center', py: 2 }}>
                      <CircularProgress size={24} color="primary" />
                    </Grid>
                  ) : latestRecord ? (
                    getLatestVitalSigns().map((item, index) => (
                      <Grid item xs={6} sm={3} key={index}>
                        <HealthAppContainer>
                          <Box sx={{ textAlign: 'center' }}>
                            <AppIconContainer color={item.color} sx={{ mx: 'auto' }}>
                              {item.icon}
                            </AppIconContainer>
                            <Typography
                              variant="body2"
                              gutterBottom
                              sx={{
                                color: theme.palette.actualMode === 'wallpaper'
                                  ? WALLPAPER_COLORS.textSecondary
                                  : 'text.secondary'
                              }}
                            >
                              {item.title}
                            </Typography>
                            <Typography
                              variant="body1"
                              fontWeight={600}
                              sx={{
                                color: theme.palette.actualMode === 'wallpaper' ? WALLPAPER_COLORS.text : 'inherit'
                              }}
                            >
                              {item.value}
                            </Typography>
                          </Box>
                        </HealthAppContainer>
                      </Grid>
                    ))
                  ) : (
                    <Grid item xs={12} sx={{ textAlign: 'center', py: 2 }}>
                      <Typography
                        variant="body2"
                        sx={{
                          color: theme.palette.actualMode === 'wallpaper'
                            ? WALLPAPER_COLORS.textSecondary
                            : 'text.secondary'
                        }}
                      >
                        {t('healthSummary.noData')}
                      </Typography>
                    </Grid>
                  )}
                </Grid>

                {/* Último diagnóstico y tratamiento */}
                {latestRecord && latestRecord.diagnosis && (
                  <Box sx={{ mt: 3, mb: 2 }}>
                    <Typography variant="h6" fontWeight={600} gutterBottom>
                      {t('healthSummary.latestDiagnosis')}
                    </Typography>
                    <HealthAppContainer sx={{ p: 2 }}>
                      <Typography variant="body1" fontWeight={500} gutterBottom>
                        {latestRecord.diagnosis}
                      </Typography>
                      {latestRecord.treatment && (
                        <>
                          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                            {t('healthSummary.treatment')}
                          </Typography>
                          <Typography variant="body2">
                            {latestRecord.treatment}
                          </Typography>
                        </>
                      )}
                    </HealthAppContainer>
                  </Box>
                )}

                {/* Sección de Citas Médicas (reemplazando Upcoming Events) */}
                <Typography
                  variant="h6"
                  fontWeight={600}
                  sx={{
                    mt: 3,
                    mb: 2,
                    color: theme.palette.actualMode === 'wallpaper' ? WALLPAPER_COLORS.text : 'inherit'
                  }}
                >
                  {t('healthSummary.appointments')}
                </Typography>
                <Grid container spacing={2}>
                  {loading ? (
                    <Grid item xs={12} sx={{ textAlign: 'center', py: 2 }}>
                      <CircularProgress size={24} color="primary" />
                    </Grid>
                  ) : appointments.upcoming && appointments.upcoming.length > 0 ? (
                    appointments.upcoming.slice(0, 2).map((appointment, index) => (
                      <Grid item xs={12} sm={6} key={appointment.appointmentId || index}>
                        <HealthAppContainer>
                          <Box>
                            <Typography
                              variant="body2"
                              gutterBottom
                              sx={{
                                color: theme.palette.actualMode === 'wallpaper'
                                  ? WALLPAPER_COLORS.textSecondary
                                  : 'text.secondary'
                              }}
                            >
                              {formatAppointmentDate(appointment.date)}
                            </Typography>
                            <Typography
                              variant="body1"
                              fontWeight={500}
                              sx={{
                                color: theme.palette.actualMode === 'wallpaper' ? WALLPAPER_COLORS.text : 'inherit'
                              }}
                            >
                              {appointment.title || 'Consulta médica'}
                            </Typography>
                            <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
                              <Chip
                                label={appointment.status || t('status.confirmed')}
                                size="small"
                                sx={{
                                  bgcolor: '#4CAF5020',
                                  color: HEALTH_GREEN,
                                  fontWeight: 500,
                                  mr: 1
                                }}
                              />
                              {appointment.professionalName && (
                                <Typography
                                  variant="caption"
                                  sx={{
                                    color: theme.palette.actualMode === 'wallpaper'
                                      ? WALLPAPER_COLORS.textSecondary
                                      : 'text.secondary'
                                  }}
                                >
                                  Dr. {appointment.professionalName}
                                </Typography>
                              )}
                            </Box>
                          </Box>
                        </HealthAppContainer>
                      </Grid>
                    ))
                  ) : (
                    <Grid item xs={12}>
                      <HealthAppContainer>
                        <Box sx={{ textAlign: 'center', py: 1 }}>
                          <Typography
                            variant="body1"
                            sx={{
                              color: theme.palette.actualMode === 'wallpaper'
                                ? WALLPAPER_COLORS.textSecondary
                                : 'text.secondary'
                            }}
                          >
                            {t('healthSummary.noAppointments')}
                          </Typography>
                        </Box>
                      </HealthAppContainer>
                    </Grid>
                  )}
                </Grid>
              </CardContent>
              <CardActions sx={{ p: 2 }}>
                {latestRecord && (
                  <Button 
                    endIcon={<ChevronRight size={16} />}
                    sx={{ textTransform: 'none', fontWeight: 600, color: "#4CAF50" }}
                    onClick={() => handleOpenDetailDialog(latestRecord)}
                  >
                    {t('healthSummary.viewReport')}
                  </Button>
                )}
                <Button 
                  endIcon={<ChevronRight size={16} />}
                  sx={{ textTransform: 'none', fontWeight: 600, color: "#4CAF50", ml: latestRecord ? 2 : 0 }}
                  onClick={() => setTabValue(1)} // Navega a la pestaña de registros médicos
                >
                  {t('healthSummary.viewRecords')}
                </Button>
              </CardActions>
            </Card>
          </Grid>
        </Grid>

        <Conditions />
      </TabPanel>

      {/* Contenido de la pestaña de Registros Médicos - Ahora con un diseño más limpio */}
      <TabPanel value={tabValue} index={1}>
        <SectionTitle variant="h5">
          {t('medicalRecords.title')}
        </SectionTitle>

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
            <CircularProgress color="primary" />
          </Box>
        ) : records.length > 0 ? (
          <Grid container spacing={2}>
            {records.map((record, index) => (
              <Grid item xs={12} sm={6} key={record.recordId || index}>
                <HealthCard>
                  <CardContent>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                      <Box>
                        <Typography variant="h6" fontWeight={600}>
                          {record.diagnosis || t('medicalRecords.record')}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                          {formatDateFull(record.date)}
                        </Typography>
                      </Box>
                    </Box>
                    
                    {/* Signos vitales en grid de 2x2 - Solo los más importantes */}
                    <Grid container spacing={1} sx={{ mt: 1 }}>
                      {record.vitalSigns && record.vitalSigns.heartRate && (
                        <Grid item xs={6}>
                          <HealthMetric>
                            <IconContainer color="#4CAF50">
                              <HeartPulse size={16} />
                            </IconContainer>
                            <Box>
                              <Typography variant="caption" color="text.secondary">{t('vitalSigns.heartRate')}</Typography>
                              <Typography variant="body2" fontWeight={600}>{record.vitalSigns.heartRate} {t('units.bpm')}</Typography>
                            </Box>
                          </HealthMetric>
                        </Grid>
                      )}
                      
                      {record.vitalSigns && (record.vitalSigns.systolicBP || record.vitalSigns.diastolicBP) && (
                        <Grid item xs={6}>
                          <HealthMetric>
                            <IconContainer color="#FF5722">
                              <Activity size={16} />
                            </IconContainer>
                            <Box>
                              <Typography variant="caption" color="text.secondary">{t('vitalSigns.bloodPressure')}</Typography>
                              <Typography variant="body2" fontWeight={600}>
                                {record.vitalSigns.systolicBP || '--'}/{record.vitalSigns.diastolicBP || '--'} {t('units.mmHg')}
                              </Typography>
                            </Box>
                          </HealthMetric>
                        </Grid>
                      )}
                      
                      {record.vitalSigns && record.vitalSigns.bloodGlucose && (
                        <Grid item xs={6}>
                          <HealthMetric>
                            <IconContainer color="#2196F3">
                              <Zap size={16} />
                            </IconContainer>
                            <Box>
                              <Typography variant="caption" color="text.secondary">{t('vitalSigns.bloodGlucose')}</Typography>
                              <Typography variant="body2" fontWeight={600}>{record.vitalSigns.bloodGlucose} {t('units.mgdl')}</Typography>
                            </Box>
                          </HealthMetric>
                        </Grid>
                      )}
                      
                      {record.vitalSigns && record.vitalSigns.oxygenSaturation && (
                        <Grid item xs={6}>
                          <HealthMetric>
                            <IconContainer color="#9C27B0">
                              <Zap size={16} />
                            </IconContainer>
                            <Box>
                              <Typography variant="caption" color="text.secondary">{t('vitalSigns.oxygenSaturation')}</Typography>
                              <Typography variant="body2" fontWeight={600}>{record.vitalSigns.oxygenSaturation}{t('units.percentage')}</Typography>
                            </Box>
                          </HealthMetric>
                        </Grid>
                      )}
                    </Grid>
                    
                    {/* Solo un pequeño resumen del tratamiento */}
                    {record.treatment && (
                      <Box sx={{ mt: 2 }}>
                        <Typography variant="body2" color="text.secondary">
                          {t('healthSummary.treatment')}
                        </Typography>
                        <Typography variant="body2" noWrap>
                          {record.treatment.length > 60 ? 
                            `${record.treatment.substring(0, 60)}...` : 
                            record.treatment}
                        </Typography>
                      </Box>
                    )}
                  </CardContent>
                  <CardActions>
                    <Button 
                      size="small" 
                      sx={{ textTransform: 'none', color: "#4CAF50" }}
                      endIcon={<ChevronRight size={16} />}
                      onClick={() => handleOpenDetailDialog(record)}
                    >
                      {t('medicalRecords.viewDetails')}
                    </Button>
                  </CardActions>
                </HealthCard>
              </Grid>
            ))}
          </Grid>
        ) : (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <Typography variant="body1" color="text.secondary">
              {t('medicalRecords.noRecords')}
            </Typography>
          </Box>
        )}
      </TabPanel>

      {/* Contenido de la pestaña de Tendencias - Ahora con más métricas y mejor navegación */}
      <TabPanel value={tabValue} index={2}>
        <SectionTitle variant="h5">
          {t('trends.title')}
        </SectionTitle>
        
        {/* Barra de navegación para métricas de tendencias */}
        <TrendNavigationBar activeMetric={activeMetric} onChangeMetric={setActiveMetric} />
        
        <Box sx={{ mb: 2 }}>
          <Card sx={{
            borderRadius: 2,
            backgroundColor: theme.palette.actualMode === 'wallpaper'
              ? WALLPAPER_COLORS.bg
              : (theme.palette.mode === 'dark' ? '#1C1C1E' : '#FFFFFF'),
            backdropFilter: theme.palette.actualMode === 'wallpaper' ? 'blur(20px)' : 'none',
            WebkitBackdropFilter: theme.palette.actualMode === 'wallpaper' ? 'blur(20px)' : 'none',
            border: theme.palette.actualMode === 'wallpaper'
              ? `1px solid ${WALLPAPER_COLORS.border}`
              : `1px solid ${theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.06)'}`,
            boxShadow: theme.palette.actualMode === 'wallpaper' ? 'none' : undefined
          }}>
            <CardContent>
              <Typography variant="h6" sx={{
                mb: 2,
                color: theme.palette.actualMode === 'wallpaper' ? WALLPAPER_COLORS.text : undefined
              }}>
                {getMetricTitle(activeMetric)}
              </Typography>
              
              {/* Navegación de períodos de tiempo */}
              <Box sx={{
                display: 'flex',
                justifyContent: 'flex-end',
                mb: 2,
                background: theme.palette.actualMode === 'wallpaper'
                  ? WALLPAPER_COLORS.bg
                  : (theme.palette.mode === 'dark' ? 'rgba(40, 40, 42, 0.94)' : 'rgba(242, 242, 247, 1)'),
                backdropFilter: theme.palette.actualMode === 'wallpaper' ? 'blur(10px)' : 'none',
                WebkitBackdropFilter: theme.palette.actualMode === 'wallpaper' ? 'blur(10px)' : 'none',
                borderRadius: '20px',
                padding: '4px',
                width: 'fit-content',
                marginLeft: 'auto'
              }}>
                <Button
                  size="small"
                  variant="text"
                  onClick={() => setTrendPeriod('week')}
                  sx={{
                    mr: 0.5,
                    bgcolor: trendPeriod === 'week'
                      ? (theme.palette.actualMode === 'wallpaper'
                        ? 'rgba(255, 255, 255, 0.3)'
                        : (theme.palette.mode === 'dark' ? 'rgba(118, 118, 128, 0.24)' : '#FFFFFF'))
                      : 'transparent',
                    color: theme.palette.actualMode === 'wallpaper'
                      ? (trendPeriod === 'week' ? WALLPAPER_COLORS.text : WALLPAPER_COLORS.textSecondary)
                      : (trendPeriod === 'week' ? '#4CAF50' : (theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.7)' : '#666666')),
                    borderRadius: '16px',
                    minWidth: '60px',
                    textTransform: 'capitalize',
                    '&:hover': {
                      bgcolor: trendPeriod === 'week'
                        ? (theme.palette.actualMode === 'wallpaper'
                          ? 'rgba(255, 255, 255, 0.3)'
                          : (theme.palette.mode === 'dark' ? 'rgba(118, 118, 128, 0.24)' : '#FFFFFF'))
                        : 'rgba(255, 255, 255, 0.1)',
                    }
                  }}
                >
                  {t('trends.week')}
                </Button>
                <Button
                  size="small"
                  variant="text"
                  onClick={() => setTrendPeriod('month')}
                  sx={{
                    mr: 0.5,
                    bgcolor: trendPeriod === 'month'
                      ? (theme.palette.actualMode === 'wallpaper'
                        ? 'rgba(255, 255, 255, 0.3)'
                        : (theme.palette.mode === 'dark' ? 'rgba(118, 118, 128, 0.24)' : '#FFFFFF'))
                      : 'transparent',
                    color: theme.palette.actualMode === 'wallpaper'
                      ? (trendPeriod === 'month' ? WALLPAPER_COLORS.text : WALLPAPER_COLORS.textSecondary)
                      : (trendPeriod === 'month' ? '#4CAF50' : (theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.7)' : '#666666')),
                    borderRadius: '16px',
                    minWidth: '60px',
                    textTransform: 'capitalize',
                    '&:hover': {
                      bgcolor: trendPeriod === 'month'
                        ? (theme.palette.actualMode === 'wallpaper'
                          ? 'rgba(255, 255, 255, 0.3)'
                          : (theme.palette.mode === 'dark' ? 'rgba(118, 118, 128, 0.24)' : '#FFFFFF'))
                        : 'rgba(255, 255, 255, 0.1)',
                    }
                  }}
                >
                  {t('trends.month')}
                </Button>
                <Button
                  size="small"
                  variant="text"
                  onClick={() => setTrendPeriod('year')}
                  sx={{
                    bgcolor: trendPeriod === 'year'
                      ? (theme.palette.actualMode === 'wallpaper'
                        ? 'rgba(255, 255, 255, 0.3)'
                        : (theme.palette.mode === 'dark' ? 'rgba(118, 118, 128, 0.24)' : '#FFFFFF'))
                      : 'transparent',
                    color: theme.palette.actualMode === 'wallpaper'
                      ? (trendPeriod === 'year' ? WALLPAPER_COLORS.text : WALLPAPER_COLORS.textSecondary)
                      : (trendPeriod === 'year' ? '#4CAF50' : (theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.7)' : '#666666')),
                    borderRadius: '16px',
                    minWidth: '60px',
                    textTransform: 'capitalize',
                    '&:hover': {
                      bgcolor: trendPeriod === 'year'
                        ? (theme.palette.actualMode === 'wallpaper'
                          ? 'rgba(255, 255, 255, 0.3)'
                          : (theme.palette.mode === 'dark' ? 'rgba(118, 118, 128, 0.24)' : '#FFFFFF'))
                        : 'rgba(255, 255, 255, 0.1)',
                    }
                  }}
                >
                  {t('trends.year')}
                </Button>
              </Box>
              
              {loading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
                  <CircularProgress color="primary" />
                </Box>
              ) : trends[activeMetric]?.length > 0 ? (
                <Box sx={{ height: 300, position: 'relative' }}>
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart
                      data={trends[activeMetric]}
                      margin={{
                        top: 5,
                        right: 30,
                        left: 20,
                        bottom: 5,
                      }}
                    >
                      <CartesianGrid
                        strokeDasharray="3 3"
                        stroke={theme.palette.actualMode === 'wallpaper' ? WALLPAPER_COLORS.border : undefined}
                      />
                      <XAxis
                        dataKey="date"
                        tickFormatter={(date) => format(parseISO(date), 'dd/MM')}
                        stroke={theme.palette.actualMode === 'wallpaper' ? WALLPAPER_COLORS.text : undefined}
                      />
                      <YAxis
                        stroke={theme.palette.actualMode === 'wallpaper' ? WALLPAPER_COLORS.text : undefined}
                      />
                      <Tooltip 
                        labelFormatter={(date) => format(parseISO(date), 'dd/MM/yyyy HH:mm')}
                        formatter={(value) => {
                          const units = {
                            heartRate: t('units.bpm'),
                            bloodGlucose: t('units.mgdl'),
                            systolicBP: t('units.mmHg'),
                            diastolicBP: t('units.mmHg'),
                            oxygenSaturation: t('units.percentage'),
                            temperature: t('units.celsius'),
                            weight: t('units.kg'),
                            height: t('units.cm')
                          };
                          return [value, units[activeMetric] || ''];
                        }}
                      />
                      {/* Línea sin el área con gradiente */}
                      <Line
                        type="monotone"
                        dataKey="value"
                        stroke={theme.palette.actualMode === 'wallpaper' ? WALLPAPER_COLORS.text : "#4CAF50"}
                        activeDot={{ r: 8 }}
                        strokeWidth={2}
                        dot={{ r: 4 }}
                      />
                    </LineChart>
                  </ResponsiveContainer>
                </Box>
              ) : (
                <Box sx={{ textAlign: 'center', py: 4 }}>
                  <Typography variant="body1" sx={{
                    color: theme.palette.actualMode === 'wallpaper' ? WALLPAPER_COLORS.textSecondary : 'text.secondary'
                  }}>
                    {t('trends.noData')}
                  </Typography>
                </Box>
              )}
            </CardContent>
          </Card>
        </Box>
      </TabPanel>

      {/* Tab de Medicamentos */}
      <TabPanel value={tabValue} index={3}>
        <Medications />
      </TabPanel>

      {/* Tab de Suplementos */}
      <TabPanel value={tabValue} index={4}>
        <Supplements />
      </TabPanel>

      {/* Diálogo de detalles del registro médico */}
      <MedicalRecordDetailDialog
        open={detailDialogOpen}
        record={selectedRecord}
        onClose={handleCloseDetailDialog}
      />

      <AGTFloatingChat />

    </PageContainer>
  );
};

export default PatientView;
